/* eslint-disable multiline-ternary */

import PropTypes from 'prop-types'
import { memo, useEffect, useMemo, useState } from 'react'
// Added import for Thirdweb's ConnectButton and inAppWallet
// import { polygon } from 'thirdweb/chains'
// import { ConnectButton } from 'thirdweb/react'
import { ConnectEmbed, useActiveAccount } from 'thirdweb/react'
import { useConnect } from 'wagmi'
import constants from 'app/constants'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
// Added import for Thirdweb client
// import { thirdwebClient as client, wallets } from 'app/thread'
import { thirdwebClient, wallets } from 'app/thread'
import Content from 'components/content/Content/Content'
import Layout from 'components/core/Layout/Layout'
import Note from 'components/core/Note/Note'
import Accordion from 'components/elements/Accordion/Accordion'
import ButtonActivity from 'components/elements/ButtonActivity/ButtonActivity'
import GradientText from 'components/elements/GradientText/GradientText'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import useLogger from 'lib/util/useLogger'
import styles from './Connect.module.scss'

const debug = useLogger('Connect', '#FA8072')

const labelNoAccount = "Don't have an account? Select an option above."

/**
 * The `Connect`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Connect (props) {
  const { className, isSignUp } = props
  const { isModalContentActive } = useModalContext()
  const { loginUser } = useUserContext()
  const account = useActiveAccount()
  const { connectors, isLoading, pendingConnector } = useConnect()
  const [isWalletOptionsShown, setIsWalletOptionsShown] = useState(false)
  const [showModal] = useState(false)
  const signInLabel = useMemo(
    () => (isSignUp ? 'Sign Up' : 'Sign In'),
    [isSignUp]
  )

  const content = useMemo(
    () => [
      {
        type: 'standard',
        align: 'center',
        content:
          '<p>Please <span class="text-gradient">select an option</span> to continue.</p>'
      }
    ],
    []
  )

  const onConnect = async wallet => {
    const response = await wallet.getAccount()

    loginUser('inApp')
    debug.log('ConnectEmbed<> => onConnect() => response', response)
    // const signature = await response.signMessage();
    // console.log(signature);
  }

  useEffect(() => {
    if (account) {
      loginUser('inApp')
    }
  }, [account])

  return showModal ? (
    <div className={`${styles.connect} ${className}`.trimEnd()}>
      <ConnectEmbed
        client={thirdwebClient}
        wallets={wallets}
        modalSize="compact"
        onConnect={onConnect}
      />
    </div>
  ) : (
    <div className={`${styles.connect} ${className}`.trimEnd()}>
      <Layout.Body>
        <Content
          className={styles.content}
          items={content}
          isActive={isModalContentActive}
        />
        <div className={styles.connectOptions}>
          {constants.IS_THIRDWEB_ENABLED && (
            <div>
              {/* TODO: Replace ConnectButton */}
              <ButtonActivity
                size="large"
                color="a"
                onClick={() => loginUser('inApp')}
                useHighlight={true}
                borderRadius="c"
              >
                {signInLabel}
              </ButtonActivity>
            </div>
          )}
          <Accordion.Item expanded={!isWalletOptionsShown} controlled={true}>
            <Accordion.Content>
              <div className={styles.walletPick}>
                <ButtonActivity
                  size="large"
                  color="c"
                  onClick={() => setIsWalletOptionsShown(true)}
                  side="left"
                  icon="wallet"
                  useHighlight={true}
                  borderRadius="c"
                >
                  {signInLabel} with Wallet
                </ButtonActivity>
              </div>
            </Accordion.Content>
          </Accordion.Item>
          <Accordion.Item expanded={isWalletOptionsShown} controlled={true}>
            <Accordion.Content>
              <div className={styles.walletOptions}>
                <GradientText Tag="h2" className={styles.walletOptionsTitle}>
                  Select an option...
                </GradientText>
                <div className={styles.walletOptionsList}>
                  {connectors.map(connector => (
                    <ButtonActivity
                      size="large"
                      color={connector.id === 'injected' ? 'a' : 'c'}
                      borderRadius="c"
                      key={connector.id}
                      icon={connector.id}
                      side="left"
                      {...(!connector.ready
                        ? {
                            Tag: 'a',
                            target: '_blank',
                            href: 'https://metamask.io/'
                          }
                        : {
                            onClick: () => {
                              loginUser(connector.id)
                            }
                          })}
                    >
                      {connector.name}
                      {isLoading &&
                        connector.id === pendingConnector?.id &&
                        ' (connecting)'}
                    </ButtonActivity>
                  ))}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </div>
        {!isSignUp && (
          <Note className={styles.returning}>{labelNoAccount}</Note>
        )}
      </Layout.Body>
    </div>
  )
}

/** @type {object} */
Connect.propTypes = {
  className: PropTypes.string,
  isSignUp: PropTypes.bool
}

/** @type {object} */
Connect.defaultProps = {
  className: '',
  isSignUp: false
}

// Memoize
export default memo(Connect)
